import { formatInTimeZone } from "date-fns-tz";
import { enGB } from "date-fns/locale";
import React from "react";

export type PromiseStatuses = 'idle' | 'loading' | 'successfully' | 'failed'
export type OnButtonClick = React.MouseEventHandler<HTMLButtonElement> | undefined
export type OnMenuItemClick = React.MouseEventHandler<HTMLLIElement> | undefined
export type OnChange = React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> | undefined
export type OnFocus = React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> | undefined
export type OnKeyDown = React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement> | undefined
export type Operation = 'save' | 'edit'
export const BACKEND_HOST = 'http://localhost:8080'
export type QuarkusError = { message: string }[]
export interface OptionType {
    label: string
    value: string
}

export const generatePagination = (currentPage: number, totalPages: number) => {
    if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage <= 3) {
        return [1, 2, 3, '...', totalPages - 1, totalPages];
    }
    if (currentPage >= totalPages - 2) {
        return [1, 2, '...', totalPages - 2, totalPages - 1, totalPages];
    }
    return [
        1,
        '...',
        currentPage - 1,
        currentPage,
        currentPage + 1,
        '...',
        totalPages,
    ];
};

export function removeQuotes(str: string): string {
    if (str.startsWith('"') && str.endsWith('"')) {
        return str.slice(1, -1);
    }
    return str;
}

export function kelvinToCelsius(kelvin: number): number {
    return Math.round(kelvin - 273.15);
}

export function degreesToDirection(degrees: number): string {
    const directions = [
        "N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE",
        "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"
    ];

    const index = Math.round(degrees / 22.5) % 16;
    return directions[index];
}

export function msTokmh(ms: number): number {
    return Math.round(ms * 3.6);
}

export function knotsToKmh(knots: number): number {
    const KMH_PER_KNOT = 1.852;
    return knots * KMH_PER_KNOT;
}

export function formatDelay(delay: string) {
    if (!delay) return null;

    const delayStr = delay.toString();
    const isEarly = delayStr.startsWith("-");

    const cleanedStr = delayStr.replace("-", "");

    let hours = 0;
    let minutes = 0;

    const match = cleanedStr.match(/(?:(\d+)h)?\s*(?:(\d+)m)?/);

    if (match) {
        hours = parseInt(match[1]) || 0;
        minutes = parseInt(match[2]) || 0;
    }

    const totalMinutes = (hours * 60) + minutes;
    if (totalMinutes === 0) return null;

    const direction = isEarly ? "early" : "delay";

    if (totalMinutes < 60) {
        return `${minutes}m ${direction}`;
    } else {
        const fullHours = Math.floor(totalMinutes / 60);
        const remainingMinutes = totalMinutes % 60;

        if (remainingMinutes === 0) {
            return `${fullHours}h ${direction}`;
        } else {
            return `${fullHours}h ${remainingMinutes}m ${direction}`;
        }
    }
}

export function getDelayColor(delay: string) {
    if (!delay) return "gray";

    const delayStr = delay.toString();
    const isEarly = delayStr.startsWith("-");

    if (isEarly) {
        return "green";
    }

    const match = delayStr.match(/(?:(\d+)h)?\s*(?:(\d+)m)?/);
    let hours = 0;
    let minutes = 0;

    if (match) {
        hours = parseInt(match[1]) || 0;
        minutes = parseInt(match[2]) || 0;
    }

    const totalMinutes = (hours * 60) + minutes;

    if (totalMinutes >= 180) {
        return "red";
    } else {
        return "orange";
    }
}


export function getMillisecondsFromMidnight(dateInput: string | Date): number {
    const date = new Date(dateInput);

    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    const seconds = date.getUTCSeconds();
    const milliseconds = date.getUTCMilliseconds();

    return (
        ((hours * 60 + minutes) * 60 + seconds) * 1000 + milliseconds
    );
}

export function formatArrivalDate(
    actualOffDateStr: string | Date,
    arrivalDelayMs: number,
    timeZone?: string
): string | null {
    if (!actualOffDateStr || arrivalDelayMs == null || !timeZone) return null;

    const baseDate = new Date(actualOffDateStr);
    const arrivalDate = new Date(baseDate.getTime() + arrivalDelayMs);

    return formatInTimeZone(arrivalDate, timeZone, "dd MMM yyyy HH:mm", {
        locale: enGB,
    });
}

export function formatFlightTime(flightTime: string): string {
    const regexHoursMinutes = /(\d+)h(\d+)m/;
    const regexMinutes = /(\d+)m/;

    let hours = 0;
    let minutes = 0;

    const matchHoursMinutes = flightTime.match(regexHoursMinutes);
    if (matchHoursMinutes) {
        hours = parseInt(matchHoursMinutes[1], 10);
        minutes = parseInt(matchHoursMinutes[2], 10);
    }
    else {
        const matchMinutes = flightTime.match(regexMinutes);
        if (matchMinutes) {
            minutes = parseInt(matchMinutes[1], 10);
        }
    }

    let result = '';
    if (hours > 0) {
        result += `${hours} hours`;
    }
    if (minutes > 0 || (hours > 0 && minutes === 0)) {
        if (result) result += ' and ';
        result += `${minutes} minutes`;
    }

    if (!result) {
        result = '-';
    }

    return result;
}

function parseDelay(delay?: string): number {
    if (!delay) return 0;
    const delayStr = delay.toString();
    const isNegative = delayStr.startsWith("-");
    const cleaned = delayStr.replace("-", "");
    const match = cleaned.match(/(?:(\d+)h)?\s*(?:(\d+)m)?/);
    const hours = match?.[1] ? parseInt(match[1], 10) : 0;
    const minutes = match?.[2] ? parseInt(match[2], 10) : 0;
    const total = hours * 60 + minutes;
    return isNegative ? -total : total;
}

function formatMinutes(totalMinutes: number): string | null {
    if (totalMinutes === 0) {
        return null;
    }
    const isEarly = totalMinutes < 0;
    const abs = Math.abs(totalMinutes);
    if (abs < 60) {
        return `${abs} min ${isEarly ? "early" : "delay"}`;
    }
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    if (m === 0) {
        return `${h}h ${isEarly ? "early" : "delay"}`;
    }
    return `${h}h ${m}m ${isEarly ? "early" : "delay"}`;
}


export function formatDelaySum(delayA?: string, delayB?: string): string | null {
    const total = parseDelay(delayA) + parseDelay(delayB);
    return formatMinutes(total);
}

export function cloudsLabel(okta: number): string {
    if (okta <= 0) return 'Clear';
    if (okta <= 2) return 'Few clouds';
    if (okta <= 4) return 'Scattered clouds';
    if (okta <= 7) return 'Broken clouds';
    return 'Overcast';
}